import React from 'react'
import { useNavigate } from 'react-router-dom'

const CookiePolicyPage: React.FC = () => {
    const navigate = useNavigate()

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">Cookie Policy</h1>
                    <p className="text-sm sm:text-base md:text-lg font-body leading-relaxed max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                        This Cookie Policy explains how SideGO uses cookies and similar technologies to
                        improve your experience on our platform.
                    </p>
                    <div className="text-xs sm:text-sm mt-3 sm:mt-4">
                        <span className="text-gray-500 dark:text-gray-400">Last updated: January 2024</span>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    <div className="prose prose-gray dark:prose-invert max-w-none">
                        <div className="space-y-6 sm:space-y-8">
                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">What Are Cookies?</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        Cookies are small text files that are placed on your device when you visit a website.
                                        They are widely used to make websites work more efficiently and to provide a better
                                        user experience.
                                    </p>
                                    <p>
                                        Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your
                                        device for a set period of time or until you delete them. Session cookies are
                                        temporary and are deleted when you close your browser.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">How We Use Cookies</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>We use cookies for the following purposes:</p>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Essential Cookies</h3>
                                    <p>
                                        These cookies are necessary for the website to function properly. They enable you to
                                        navigate the site and use its features, such as accessing secure areas.
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Authentication and login status</li>
                                        <li>Shopping cart functionality</li>
                                        <li>Security features</li>
                                        <li>Load balancing</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Performance Cookies</h3>
                                    <p>
                                        These cookies collect information about how visitors use our website, which pages
                                        are visited most often, and if users get error messages.
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Google Analytics</li>
                                        <li>Page load times</li>
                                        <li>Error tracking</li>
                                        <li>Usage statistics</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Functionality Cookies</h3>
                                    <p>
                                        These cookies remember choices you make to improve your experience and personalize
                                        the website for you.
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Language preferences</li>
                                        <li>Theme settings (dark/light mode)</li>
                                        <li>Location preferences</li>
                                        <li>User interface customizations</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Targeting Cookies</h3>
                                    <p>
                                        These cookies are used to deliver content more relevant to you and your interests.
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>Personalized gig recommendations</li>
                                        <li>Relevant advertising</li>
                                        <li>Social media integration</li>
                                        <li>Third-party content</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Types of Cookies We Use</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                                            <thead className="bg-gray-50 dark:bg-gray-800">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-ui font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Cookie Name
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-ui font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Purpose
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-ui font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Duration
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                                <tr>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-ui font-medium text-gray-900 dark:text-gray-100">
                                                        sidego_session
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        Authentication and session management
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        Session
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-ui font-medium text-gray-900 dark:text-gray-100">
                                                        theme_preference
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        Remember dark/light mode setting
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        1 year
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-ui font-medium text-gray-900 dark:text-gray-100">
                                                        _ga
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        Google Analytics tracking
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        2 years
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-ui font-medium text-gray-900 dark:text-gray-100">
                                                        location_pref
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        Remember location preferences
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                                        30 days
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Third-Party Cookies</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        We may also use third-party cookies from our partners and service providers:
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                                        <li><strong>Social Media:</strong> For social sharing and login functionality</li>
                                        <li><strong>Payment Processors:</strong> For secure payment processing</li>
                                        <li><strong>Content Delivery Networks:</strong> For faster content delivery</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Managing Your Cookies</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        You have several options for managing cookies:
                                    </p>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Browser Settings</h3>
                                    <p>
                                        Most browsers allow you to control cookies through their settings. You can:
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc text-sm sm:text-base">
                                        <li>View what cookies are stored on your device</li>
                                        <li>Delete cookies</li>
                                        <li>Block cookies from specific sites</li>
                                        <li>Block all cookies</li>
                                    </ul>

                                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mt-4 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white">Cookie Consent</h3>
                                    <p>
                                        When you first visit our website, we'll ask for your consent to use non-essential
                                        cookies. You can change your preferences at any time through our cookie settings.
                                    </p>

                                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 sm:p-5 mt-4 sm:mt-6">
                                        <p className="text-sm sm:text-base text-amber-800 dark:text-amber-200">
                                            <strong>Note:</strong> Disabling certain cookies may affect the functionality of our website
                                            and your user experience.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Updates to This Policy</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
                                    <p>
                                        We may update this Cookie Policy from time to time to reflect changes in our practices
                                        or for other operational, legal, or regulatory reasons.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-heading font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">Contact Us</h2>
                                <div className="text-sm sm:text-base font-body leading-relaxed text-gray-700 dark:text-gray-300">
                                    <p className="mb-4 sm:mb-6">
                                        If you have any questions about our use of cookies, please reach out to us through our contact form.
                                    </p>
                                    <button
                                        onClick={() => navigate('/contact')}
                                        className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors inline-block"
                                    >
                                        Contact Support
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default CookiePolicyPage